/**
 * storageHeadroom — room in localStorage for the Firestore SDK, made before it
 * starts and kept while it runs (2026-09-28, Malaika's freezes).
 *
 * Why this exists
 * ──────────────────────────────────────────────────────────────────────
 * With multi-tab persistence the Firestore SDK (@firebase/firestore 4.8.0)
 * coordinates tabs through localStorage keys (`firestore_clients_*`,
 * `firestore_targets_*`, `firestore_mutations_*`, …), written on every
 * listen, unlisten, write and acknowledgement, with no error handling. When
 * one of those writes hits the quota the error is rethrown out of the SDK's
 * async queue and the queue fails for good: listeners stop, writes throw
 * "INTERNAL ASSERTION FAILED … (ID: b815)", and reads queued behind the
 * failure never settle (the endless "picking up right where you left off").
 *
 * The chat fills localStorage with whole transcripts, one per thread. The
 * guard it has had since 2026-08-22 (GlobalChatSystem/storageQuota.ts) makes
 * room only once the chat engine has loaded, after Firestore has started,
 * and the self-heal deleted only the SDK's own tiny keys. So a nearly full
 * bin failed the SDK again straight after every refresh: a hard refresh did
 * not help, and only a fresh storage partition (Incognito, or signing out,
 * which wipes localStorage) did. Measured with the real SDK in Chromium by
 * scripts/probe-firestore-storage-full.mjs.
 *
 * What it does
 * ──────────────────────────────────────────────────────────────────────
 * 1. prepareStorageForFirestore() runs in src/utils/firestore.ts BEFORE
 *    initializeFirestore. When the bin is well used it probe-writes
 *    HEADROOM_PROBE_CHARS; if that fails it evicts per-thread transcript
 *    caches (the server holds every message) until the probe fits. If it
 *    cannot make room, the tab uses Firestore's in-memory cache, which never
 *    touches localStorage.
 * 2. installFirestoreStorageGuard() wraps Storage.prototype.setItem for the
 *    SDK's own keys only: a quota error on a `firestore_*` write evicts
 *    thread caches and retries once. If there is still no room the error is
 *    rethrown and the SDK fails as before; the next load (the heal's reload
 *    or a hard refresh) then finds no room either and starts on the
 *    in-memory cache, so it recovers.
 * 3. markFirestoreMemoryMode() is written by the heal in errorHandlers.ts
 *    when the SDK has died in this tab for any reason: the reload starts on
 *    the in-memory cache even when there is room, because the persistent
 *    cache is what broke. It lives in sessionStorage (a separate quota, and it
 *    survives a reload of the same tab) and expires after MEMORY_MODE_TTL_MS,
 *    after which the next load probes again.
 *
 * Eviction is default-deny: only `prest0_thread_msgs__*` keys are ever
 * removed — another person's caches on this device first, then caches whose
 * newest message is older than COLD_MS (largest first), then the rest (least
 * recently active first). The open thread's cache is never evicted. Auth
 * (`firebase:*`), the SDK's own keys, drafts, scheduled messages and every
 * key this module does not know are left alone. Ranking reads each cache's
 * newest `"timestamp":` from the text; nothing is JSON-parsed.
 *
 * This module imports nothing: it runs first, from the entry chunk. The
 * prefix is pinned equal to threadPersist.THREAD_MSG_KEY_PREFIX by test.
 */

/** Per-thread transcript caches: the only key class this module evicts. */
export const THREAD_CACHE_KEY_PREFIX = 'prest0_thread_msgs__';
/** Where the chat keeps each instance's open thread id. */
export const ACTIVE_THREAD_KEY_PREFIX = 'prest0_active_thread__';
/** The key the headroom probe writes and removes at once. */
export const HEADROOM_PROBE_KEY = 'prest0_fs_headroom_probe';
/** Free space the probe demands, in UTF-16 code units (~512 KB). */
export const HEADROOM_PROBE_CHARS = 262_144;
/** Below this much stored text the probe is skipped: every browser has room. */
export const PROBE_WHEN_USED_CHARS = 1_500_000;
/** Each eviction round frees at least this much before probing again. */
export const EVICTION_ROUND_CHARS = 524_288;
/** Rounds of eviction at boot before falling back to the in-memory cache. */
export const EVICTION_ROUNDS = 3;
/** A runtime reclaim frees at least this much, or 4x the refused write. */
export const GUARD_RECLAIM_MIN_CHARS = 262_144;
/** Caches whose newest message is older than this are evicted before the rest. */
export const COLD_MS = 48 * 60 * 60 * 1000;
/** sessionStorage key marking this tab to start Firestore on its in-memory cache. */
export const FIRESTORE_CACHE_MODE_KEY = 'prest0_fs_cache_mode';
/** How long the marker holds before the next load probes again. */
export const MEMORY_MODE_TTL_MS = 30 * 60 * 1000;

export type FirestoreCacheMode = 'persistent' | 'memory';

export type HeadroomReason =
  /** Enough room: nothing was evicted. */
  | 'room'
  /** Thread caches were evicted until the probe fitted. */
  | 'made-room'
  /** This tab was marked to use the in-memory cache (guard give-up or heal). */
  | 'marker'
  /** Eviction could not make room. */
  | 'no-room'
  /** localStorage cannot be used at all; the SDK decides for itself. */
  | 'storage-unavailable';

export interface HeadroomResult {
  mode: FirestoreCacheMode;
  reason: HeadroomReason;
  evictedKeys: number;
  evictedChars: number;
}

export interface EvictionOptions {
  /** Keys that must survive, beyond the open thread's cache. */
  protect?: Iterable<string>;
  /** Clock, for tests. */
  now?: number;
}

export interface EvictionResult {
  evictedKeys: number;
  evictedChars: number;
}

function localStorageOrNull(): Storage | null {
  try {
    const ls = (globalThis as { localStorage?: Storage }).localStorage;
    return ls ?? null;
  } catch {
    return null;
  }
}

function sessionStorageOrNull(): Storage | null {
  try {
    const ss = (globalThis as { sessionStorage?: Storage }).sessionStorage;
    return ss ?? null;
  } catch {
    return null;
  }
}

/** Every browser's shape of "the bin is full". */
export function isQuotaError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: unknown; code?: unknown };
  return (
    e.name === 'QuotaExceededError'
    || e.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || e.code === 22
    || e.code === 1014
  );
}

/** Stored text in UTF-16 code units (keys and values). */
export function usedChars(ls: Storage): number {
  let total = 0;
  try {
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (key === null) continue;
      const value = ls.getItem(key);
      total += key.length + (value ? value.length : 0);
    }
  } catch {
    /* a storage failure mid-count: report what was counted */
  }
  return total;
}

/**
 * Can `chars` more code units be written? Writes and removes the probe key.
 * A failure other than the quota (a storage that refuses everything) reads as
 * "no room" too: the SDK would meet the same refusal.
 */
export function probeHeadroom(ls: Storage, chars: number = HEADROOM_PROBE_CHARS): boolean {
  try {
    ls.setItem(HEADROOM_PROBE_KEY, 'x'.repeat(chars));
  } catch {
    try { ls.removeItem(HEADROOM_PROBE_KEY); } catch { /* nothing was written */ }
    return false;
  }
  try { ls.removeItem(HEADROOM_PROBE_KEY); } catch { /* the next probe overwrites it */ }
  return true;
}

/**
 * The newest `"timestamp":` value in a cached transcript, read from the text:
 * messages are stored oldest first, so the last few occurrences hold the
 * newest. 0 when none can be read (ranked coldest).
 */
export function newestTimestampInText(text: string | null, now: number = Date.now()): number {
  if (!text) return 0;
  const marker = '"timestamp":';
  let newest = 0;
  let from = text.length;
  for (let seen = 0; seen < 3; seen += 1) {
    const at = text.lastIndexOf(marker, from);
    if (at < 0) break;
    const rest = text.slice(at + marker.length, at + marker.length + 40).trimStart();
    let ms = 0;
    if (rest.startsWith('"')) {
      const end = rest.indexOf('"', 1);
      if (end > 1) ms = Date.parse(rest.slice(1, end)) || 0;
    } else {
      const digits = /^\d{10,16}/.exec(rest);
      if (digits) ms = Number(digits[0]);
    }
    // A timestamp from the future is not evidence of activity.
    if (ms > newest && ms <= now + 60_000) newest = ms;
    from = at - 1;
    if (from < 0) break;
  }
  return newest;
}

/** The uid of the person signed in on this device, from the auth SDK's own record. */
export function signedInUid(ls: Storage): string | null {
  try {
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (!key || !key.startsWith('firebase:authUser:')) continue;
      const raw = ls.getItem(key);
      if (!raw) continue;
      try {
        const uid = (JSON.parse(raw) as { uid?: unknown }).uid;
        if (typeof uid === 'string' && uid) return uid;
      } catch {
        /* a record that does not parse names nobody */
      }
    }
  } catch {
    /* enumeration failed */
  }
  return null;
}

/** Thread ids the chat has open, from every instance's active-thread key. */
function openThreadIds(ls: Storage): string[] {
  const ids: string[] = [];
  try {
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (!key || !key.startsWith(ACTIVE_THREAD_KEY_PREFIX)) continue;
      let value = ls.getItem(key);
      if (!value) continue;
      if (value.startsWith('"') && value.endsWith('"') && value.length > 2) value = value.slice(1, -1);
      if (value) ids.push(value);
    }
  } catch {
    /* partial list */
  }
  return ids;
}

type Candidate = { key: string; chars: number; rank: number; newest: number };

/** 0: another person's cache on this device. 1: cold. 2: warm. */
function rankOf(key: string, newest: number, now: number, uid: string | null): number {
  if (uid) {
    const seg = key.indexOf('__u_');
    if (seg >= 0) {
      const rest = key.slice(seg + 4);
      if (!rest.startsWith(`${uid}__`)) return 0;
    }
  }
  return newest > 0 && now - newest < COLD_MS ? 2 : 1;
}

/**
 * Remove thread caches until at least `targetChars` code units are freed, in
 * this order: another person's caches, cold caches largest first, then warm
 * caches least recently active first. Never the open thread's cache, never a
 * protected key, never any other key class.
 */
export function evictThreadCaches(ls: Storage, targetChars: number, opts: EvictionOptions = {}): EvictionResult {
  const now = opts.now ?? Date.now();
  const protect = new Set(opts.protect ?? []);
  const open = openThreadIds(ls);
  const uid = signedInUid(ls);
  const candidates: Candidate[] = [];
  try {
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (!key || !key.startsWith(THREAD_CACHE_KEY_PREFIX) || protect.has(key)) continue;
      if (open.some((id) => key.endsWith(`__${id}`))) continue;
      const value = ls.getItem(key);
      const newest = newestTimestampInText(value, now);
      candidates.push({
        key,
        chars: key.length + (value ? value.length : 0),
        rank: rankOf(key, newest, now, uid),
        newest,
      });
    }
  } catch {
    /* evict from what was listed */
  }
  candidates.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    if (a.rank === 2) return a.newest - b.newest;
    return b.chars - a.chars;
  });
  let evictedChars = 0;
  let evictedKeys = 0;
  for (const c of candidates) {
    if (evictedChars >= targetChars) break;
    try {
      ls.removeItem(c.key);
    } catch {
      continue;
    }
    evictedChars += c.chars;
    evictedKeys += 1;
  }
  return { evictedKeys, evictedChars };
}

/** Is this tab marked to start Firestore on its in-memory cache? Clears an expired marker. */
export function readFirestoreMemoryMode(now: number = Date.now()): boolean {
  const ss = sessionStorageOrNull();
  if (!ss) return false;
  try {
    const raw = ss.getItem(FIRESTORE_CACHE_MODE_KEY);
    if (!raw) return false;
    const match = /^memory:(\d+)$/.exec(raw);
    const until = match ? Number(match[1]) : 0;
    if (until > now) return true;
    ss.removeItem(FIRESTORE_CACHE_MODE_KEY);
  } catch {
    /* unreadable: persistent, as before */
  }
  return false;
}

/** Mark this tab to start Firestore on its in-memory cache for the next MEMORY_MODE_TTL_MS. */
export function markFirestoreMemoryMode(now: number = Date.now()): boolean {
  const ss = sessionStorageOrNull();
  if (!ss) return false;
  try {
    ss.setItem(FIRESTORE_CACHE_MODE_KEY, `memory:${now + MEMORY_MODE_TTL_MS}`);
    return true;
  } catch {
    return false;
  }
}

/**
 * Decide how Firestore starts in this tab, making room first when the bin is
 * nearly full. Synchronous: it runs before initializeFirestore.
 */
export function prepareStorageForFirestore(opts: { memoryFallback?: boolean; now?: number } = {}): HeadroomResult {
  const memoryFallback = opts.memoryFallback !== false;
  const now = opts.now ?? Date.now();
  if (memoryFallback && readFirestoreMemoryMode(now)) {
    return { mode: 'memory', reason: 'marker', evictedKeys: 0, evictedChars: 0 };
  }
  const ls = localStorageOrNull();
  if (!ls) return { mode: 'persistent', reason: 'storage-unavailable', evictedKeys: 0, evictedChars: 0 };
  if (usedChars(ls) < PROBE_WHEN_USED_CHARS || probeHeadroom(ls)) {
    return { mode: 'persistent', reason: 'room', evictedKeys: 0, evictedChars: 0 };
  }
  let evictedKeys = 0;
  let evictedChars = 0;
  for (let round = 0; round < EVICTION_ROUNDS; round += 1) {
    const freed = evictThreadCaches(ls, EVICTION_ROUND_CHARS, { now });
    evictedKeys += freed.evictedKeys;
    evictedChars += freed.evictedChars;
    if (probeHeadroom(ls)) return { mode: 'persistent', reason: 'made-room', evictedKeys, evictedChars };
    if (freed.evictedKeys === 0) break;
  }
  return { mode: memoryFallback ? 'memory' : 'persistent', reason: 'no-room', evictedKeys, evictedChars };
}

const GUARD_MARK = '__prest0FirestoreStorageGuard';

type GuardedSetItem = ((this: Storage, key: string, value: string) => void) & { [GUARD_MARK]?: true };

/** Is the guard installed on this realm's Storage.prototype? */
export function hasFirestoreStorageGuard(): boolean {
  try {
    return (Storage.prototype.setItem as GuardedSetItem)[GUARD_MARK] === true;
  } catch {
    return false;
  }
}

/**
 * Wrap Storage.prototype.setItem so that a quota error on the Firestore SDK's
 * own localStorage writes makes room and retries once. Every other write,
 * every other storage and every other error passes through untouched.
 * Idempotent; returns an uninstall function. Installed only by firestore.ts.
 */
export function installFirestoreStorageGuard(): () => void {
  let proto: Storage;
  try {
    proto = Storage.prototype;
  } catch {
    return () => undefined;
  }
  if (hasFirestoreStorageGuard()) return () => undefined;
  const original = proto.setItem;
  const guarded: GuardedSetItem = function guardedSetItem(this: Storage, key: string, value: string): void {
    try {
      original.call(this, key, value);
      return;
    } catch (err) {
      const k = String(key);
      if (!isQuotaError(err) || !k.startsWith('firestore_') || this !== localStorageOrNull()) throw err;
      const needed = Math.max(GUARD_RECLAIM_MIN_CHARS, 4 * (k.length + String(value).length));
      const freed = evictThreadCaches(this, needed);
      if (freed.evictedKeys > 0) {
        try {
          original.call(this, key, value);
          return;
        } catch (retryErr) {
          if (!isQuotaError(retryErr)) throw retryErr;
        }
      }
      throw err;
    }
  };
  guarded[GUARD_MARK] = true;
  proto.setItem = guarded;
  return () => {
    if (proto.setItem === guarded) proto.setItem = original;
  };
}
